import {Map, List} from 'immutable';
import type {RecordOf} from 'immutable';
import {commerceCountryList} from '@packages/systems/core/constants/SharedConfig';
import {type NetworkStatus} from './constants';
import {type ExportOptions} from './OrderCSVExportHelper';

import {type CommerceSettingsStateRecordShape} from './settings';
import {type EditorOrderPaneRecordType} from './editorOrdersPane';
import {type CommerceStatusRecordType} from './status';
import {type OrdersStateShape} from './orders';
import {type OrderStateModal} from './orderStateModal';
import {type DiscountStateType} from './discounts';
import {type XHRState, type CommerceStripeRecordType} from './recordTypes';
import {type GettingStartedStatusStateShape} from './gettingStartedStatus';

// Flow workaround using $Keys: https://github.com/facebook/flow/issues/961#issuecomment-410557152
// First, we convert the array into an Object that we can use for creating the union with $Keys.
const countries = commerceCountryList.reduce<Record<string, any>>(
  (memo, {code}) => {
    memo[code] = '';
    return memo;
  },
  {}
);

// FIXME: use $Keys<typeof unitSystems>
export type UnitSystemType = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type ValidCountriesType = keyof typeof countries;
// FIXME: use $Keys<typeof locales>
export type ValidCurrencyLocaleType = string;
export type ShippingMethodType = Map<
  'name' | 'description' | 'mode' | 'rules' | 'id',
  any
>;
export type ValidShippingZoneType = Map<
  'name' | 'countries' | 'methods' | '_id' | 'isRestOfTheWorld',
  string | List<any> | boolean
>;
export type ValidShippingZonesType = List<ValidShippingZoneType>;
export type CommercePayPalRecordProps = {
  requestStatus: NetworkStatus;
  disconnectRequestStatus: NetworkStatus;
  connected: boolean;
  paymentReceivable: boolean;
  emailVerified: boolean;
  permissionsGranted: boolean;
  paypalButtonNotificationTriggered: boolean;
  paypalPageNotificationTriggered: boolean;
};
export type CommercePayPalRecordType = RecordOf<CommercePayPalRecordProps>;

export type OrderExportModalState = {
  validationErrorMessage: string;
  errorMessage?: string;
  visible: boolean;
  options: ExportOptions;
  isExporting: boolean;
};

export type CommerceStateShape = {
  settings: CommerceSettingsStateRecordShape;
  status: CommerceStatusRecordType;
  connect: XHRState;
  orders: OrdersStateShape;
  stripe: CommerceStripeRecordType;
  paypal: CommercePayPalRecordType;
  editorOrdersPane: EditorOrderPaneRecordType;
  orderStateModal: OrderStateModal;
  orderExportModal: OrderExportModalState;
  gettingStartedStatus: GettingStartedStatusStateShape;
  discounts: DiscountStateType;
};

// Group and decimal combinations (see examples with DECIMAL_OPTIONS in commerceConstants.js)
export type ValidCurrencyFormatType =
  | ',.'
  | '.,'
  | '٬,'
  | '`.'
  | ' ,'
  | ' .'
  | ' $'
  | ','
  | '.'
  | '`'
  | '٬'
  | ' '
  | '';

export type PaymentGatewaySupportType = Map<
  'currencyConflict' | 'countryConflict',
  boolean
>;

type Reactor = Map<string, any>;
export type Driver = Map<string, any>;
type Category = Map<string, any>;

export type CommerceReactorState = RecordOf<{
  reactors: Map<string, Reactor>;
  drivers: Map<string, Driver>;
  categories: Map<string, Category>;
  currentDriver: Driver | null;
}>;
